import React, { FC, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { login } from "../auth/authActions";
import axios from "axios";
import { startLoading, endLoading } from "../state/appActions";
import { signup, getUser, getRegistrationUser, getGoogleLoginURL } from "../api/helpers";
import { GetUser, QuickOrg } from "../types";
import { setUserData } from "../auth/authActions";
import { SignupFormData } from "../types";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "./styles/globals.css";

const OrgEqSignup: FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [org, setOrg] = useState<QuickOrg>({
    name: "",
    id: "",
  });
  
  const [formData, setFormData] = useState<SignupFormData>({
    email: "",
    first_name: "",
    organization_name: "",
    last_name: "",
    organization_id: "",
    role: "",
    industry: "",
    free_trial: false,
    password: "",
    default_method_name: "MEDDPICC",
    passwordConfirm: "",
    linkedin_url: "",
    termsChecked: false,
    completed_intro_tour: false,
  });

  const queryParams = new URLSearchParams(location.search);
  const redirect = queryParams.get("redirect");
  const token = queryParams.get("token");
  const invite = queryParams.get("invite");
  const admin = queryParams.get("a");
  const freeTrial = queryParams.get("ft");

  const isAuthenticated = useSelector(
    (state: any) => state.auth.isAuthenticated
  );

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate("/org-eq#dashboard");
      return;
    }

    // Load registration user data if token is provided
    const loadRegistrationData = async () => {
      if (token && invite) {
        try {
          const response = await getRegistrationUser(token);
          setOrg({
            name: response.organization_name,
            id: response.organization_id,
          });
          setFormData((prev) => ({
            ...prev,
            email: response.email,
            organization_id: response.organization_id,
          }));
        } catch (err) {
          setError("Invalid or expired invitation link.");
        }
      }
    };

    if (admin === "t") {
      setFormData((prev) => ({
        ...prev,
        role: "admin",
      }));
    }

    loadRegistrationData();
  }, [token, invite, admin, isAuthenticated, navigate]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate step 1 fields
    if (!formData.first_name || !formData.last_name) {
      setError("Please enter your first and last name.");
      return;
    }
    if (!formData.email) {
      setError("Please enter your email address.");
      return;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (formData.password !== formData.passwordConfirm) {
      setError("Passwords do not match.");
      return;
    }

    setStep(2);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.termsChecked) {
      setError("Please accept the terms and conditions.");
      return;
    }

    if (
      !formData.role ||
      !formData.linkedin_url ||
      (!formData.organization_name && !formData.organization_id)
    ) {
      setError("Please fill out all required fields.");
      return;
    }

    dispatch(startLoading());
    try {
      let newData = { ...formData };
      if (admin === "t") {
        newData.role = "admin";
      }
      if (freeTrial === "t") {
        newData.free_trial = true;
      }

      const response = await signup(newData);
      if (axios.isAxiosError(response)) {
        setError(response?.response?.data?.message || "Signup failed. Please try again.");
      } else if (response?.status === "success") {
        dispatch(login());
        const userData: GetUser = await getUser(dispatch);
        dispatch(setUserData(userData.user));
        
        if (redirect) {
          navigate(redirect);
        } else {
          navigate("/org-eq#dashboard");
        }
      } else {
        setError(response?.message || "Signup failed. Please try again.");
      }
    } catch (err: any) {
      localStorage.removeItem("userToken");
      console.error("Signup error:", err);
      setError(
        "An error occurred during signup. Please refresh and try again."
      );
    } finally {
      dispatch(endLoading());
    }
  };

  const handleGoogleSignUp = async () => {
    dispatch(startLoading());
    localStorage.setItem('orgEqLogin', 'true');
    const response = await getGoogleLoginURL(org?.id);
    if (response) {
      window.location.assign(response.redirect_url);
    } else {
      dispatch(endLoading());
      localStorage.removeItem('orgEqLogin');
      setError("Error loading Google authenticator. Please refresh and try again.");
    }
  };

  return (
    <div className="org-eq-dashboard min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <button
        onClick={() => navigate("/org-eq/home")}
        className="fixed top-6 left-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Return to homepage
      </button>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center">
          <img
            src="/logos/orgeq-logo.svg"
            alt="OrgEQ"
            className="w-[196px] h-auto mx-auto"
          />
          <CardTitle className="text-2xl">
            {step === 1 ? "Create your account" : "Almost there!"}
          </CardTitle>
          <CardDescription>
            {step === 1
              ? org?.name
                ? `Sign up to join ${org.name}`
                : "Sign up to get started with OrgEQ"
              : "Just a few more details to complete your profile"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 1 ? (
            <form onSubmit={handleNext} className="space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full gap-2 bg-transparent"
                onClick={handleGoogleSignUp}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Sign up with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    type="text"
                    name="first_name"
                    placeholder="John"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    type="text"
                    name="last_name"
                    placeholder="Doe"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Company Email</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  readOnly={!!org?.id}
                  className={org?.id ? "bg-gray-50 text-slate-900" : ""}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  name="password"
                  placeholder="Minimum 8 characters"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwordConfirm">Confirm Password</Label>
                <Input
                  id="passwordConfirm"
                  type="password"
                  name="passwordConfirm"
                  placeholder="Re-enter your password"
                  value={formData.passwordConfirm}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <Button type="submit" className="w-full">
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4">
              {!formData.organization_id && (
                <div className="space-y-2">
                  <Label htmlFor="organization_name">Company Name</Label>
                  <Input
                    id="organization_name"
                    type="text"
                    name="organization_name"
                    placeholder="Acme Corp"
                    value={formData.organization_name}
                    onChange={handleInputChange}
                    required={!formData.organization_id}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="linkedin_url">LinkedIn Profile URL</Label>
                <Input
                  id="linkedin_url"
                  type="url"
                  name="linkedin_url"
                  placeholder="https://www.linkedin.com/in/yourusername"
                  value={formData.linkedin_url}
                  onChange={handleInputChange}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Share your LinkedIn profile URL to help us personalize your experience.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-xs transition-colors outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                  required
                >
                  <option value="">Select your role</option>
                  <option value="Admin">Admin</option>
                  <option value="Sales Rep">Sales Rep</option>
                  <option value="Manager">Manager</option>
                  <option value="Executive">Executive</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-xs transition-colors outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                >
                  <option value="">Select industry</option>
                  <option value="Technology">Technology</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Finance">Finance</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Retail">Retail</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="termsChecked"
                  name="termsChecked"
                  checked={formData.termsChecked}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      termsChecked: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  required
                />
                <label
                  htmlFor="termsChecked"
                  className="text-sm text-muted-foreground"
                >
                  I agree to the{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    className="text-primary hover:underline"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    className="text-primary hover:underline"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(1)}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button type="submit" className="flex-1">
                  Create Account
                </Button>
              </div>
            </form>
          )}

          <div className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <button
              onClick={() => navigate("/org-eq/login")}
              className="text-primary hover:underline font-semibold"
            >
              Sign in
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrgEqSignup;
